import { useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useState } from "react";

import { api } from "../../api/client";
import { Banner } from "../../components/Callout";
import { Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t } from "../../i18n/i18n";

/** Relecture d'un document lu dans l'autre langue tant qu'une de ses traductions est en cours (docs/13 « Langue des
 *  documents ») : toutes les 2,5 s, arrêtée dès que plus rien n'attend. */
export const TRANSLATION_POLL_MS = 2500;

/** Intervalle de relecture d'un document : `TRANSLATION_POLL_MS` tant qu'il attend une traduction, sinon `otherwise`. */
export function translationInterval(pending: boolean, otherwise: number | false): number | false {
  return pending ? TRANSLATION_POLL_MS : otherwise;
}

/** Bandeaux de la traduction d'un document lu dans l'autre langue que sa langue de rédaction : « Traduction… » (points
 *  animés) tant qu'une partie attend sa traduction, puis rien ; une traduction impossible laisse l'avis discret
 *  « Traduction indisponible » et « Réessayer » (le texte de repli reste affiché). Apparitions fluides (`Presence`). */
export function TranslationBanner({
  exerciseId,
  translating,
  untranslated,
}: {
  exerciseId: string | null | undefined;
  translating: boolean;
  untranslated: boolean;
}) {
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);
  const retry = async () => {
    if (!exerciseId) return;
    setRetrying(true);
    try {
      await api.retryTranslations(exerciseId);
      await queryClient.invalidateQueries();
    } finally {
      setRetrying(false);
    }
  };
  const failed = untranslated && !translating;
  return (
    <>
      <Presence show={translating}>
        {translating && (
          <Banner tone="run">
            <Waiting>{t("Traduction…")}</Waiting>
          </Banner>
        )}
      </Presence>
      <Presence show={failed}>
        {failed && (
          <Banner
            tone="neutral"
            actions={
              <Button size="sm" variant="secondary" onClick={() => void retry()} disabled={retrying || !exerciseId}>
                <RotateCcw size={14} /> {retrying ? <Waiting>{t("Nouvel essai…")}</Waiting> : t("Réessayer")}
              </Button>
            }
          >
            {t("Traduction indisponible : une partie du document garde son texte d'origine.")}
          </Banner>
        )}
      </Presence>
    </>
  );
}
